import TooltipCustom from 'ui/atoms/Tooltip/Tooltip';
import clsx from 'clsx';
import AiProgramTag from 'core/modules/atoms/AiProgramTag/AiProgramTag';
import { hasTangerineAi } from 'core/modules/atoms/AiProgramTag/hasTangerineAi';
import { useComingSoon } from 'core/lite/hooks/useComingSoon';
import { normalizeContentLang } from 'core/utils/contentLang';
import PropTypes from 'prop-types';
import React from 'react';
import { withTranslation } from 'react-i18next';
import { Avatar, Icon } from 'ui';
import ProgramImage from '../../general/ProgramImage/ProgramImage';

const BookCard = ({
  canShowAiTag = false,
  has_tangerine_ai,
  program,
  program_guid,
  isEdited,
  versionAuthorAvatar,
  versionName,
  image,
  title,
  view,
  t,
  selected,
  onChangeSelected,
  borderRadiusSize,
  onClick,
  customRight,
  language,
  configProgramPicture,
  isAccessible,
}) => {
  const { showComingSoon } = useComingSoon();
  const shouldShowAiTag = canShowAiTag && hasTangerineAi({ has_tangerine_ai, program });
  const [animation, setAnimation] = React.useState(0);
  const contentLang = normalizeContentLang(language);

  function handleSelectClick(e) {
    if (isAccessible) {
      setAnimation(1);
      onChangeSelected(e);
    } else {
      showComingSoon();
    }
  }

  function handleActionClick() {
    onClick(program_guid, isAccessible);
  }

  function onSelectAnimationEnd() {
    setAnimation(0);
  }

  const className = clsx(`book-card book-card--${view}`, { 'book-card--selected': selected }, `book-card--border-radius-${borderRadiusSize}`);

  const content = (
    <>
      <div className="book-card__image">
        <ProgramImage course_picture={image} configProgramPicture={configProgramPicture} />
        {isEdited && <div className="program-card__edit">{t('course:Edited')}</div>}
      </div>
      <div className="book-card__info">
        {language && <span className="book-card__language">{t(`languages:${language}`)}</span>}
        <span className="book-card__title">
          <TooltipCustom title={title} placement="top">
            <div lang={contentLang}>{title}</div>
          </TooltipCustom>
        </span>
        {shouldShowAiTag && <AiProgramTag variant="student" />}
        {(versionAuthorAvatar || versionName) && (
          <div className="program-card__info-bottom">
            <div className="program-card__version-avatar">
              <Avatar size="sm" image={versionAuthorAvatar} />
            </div>
            {versionName && <span className="program-card__version-name">{t('course:My version')}</span>}
          </div>
        )}

        {view === 'selectable' &&
          (isAccessible ? (
            <div className="book-card__selects-icon" data-animation={animation} onAnimationEnd={onSelectAnimationEnd}>
              <Icon name={selected ? 'check-checked-round-fill' : 'add-circle'} color={'primary'} size="3xl" />
            </div>
          ) : (
            <div className="book-card__coming-soon">{t('common:Coming soon')}</div>
          ))}

        {view === 'custom-right' && <div className="book-card__button"> {customRight} </div>}
      </div>
    </>
  );

  // custom-right: la card no es clicable en su conjunto; la acción vive en customRight
  // (p. ej. un botón "Enviar"), que ya es accesible por sí mismo.
  if (view === 'custom-right') {
    return <div className={className}>{content}</div>;
  }

  // selectable / selectable disabled: toggle de selección de libro (o "coming soon" si no
  // accesible). El estado "disabled" (libro ya asignado) no es accionable ni tabulable.
  if (view === 'selectable' || view === 'selectable disabled') {
    const isDisabled = view === 'selectable disabled';
    // aria-pressed solo cuando es un toggle real: no cuando está deshabilitado ni cuando no es
    // accesible (en ese caso handleSelectClick solo muestra "coming soon", sin cambiar selected).
    const shouldExposePressed = !isDisabled && isAccessible;
    return (
      <button
        aria-pressed={shouldExposePressed ? selected : undefined}
        className={className}
        disabled={isDisabled}
        onClick={handleSelectClick}
        type="button"
      >
        {content}
      </button>
    );
  }

  // resto (default…): acción sobre el libro (abrir/navegar) vía onClick. Sin onClick la card es
  // informativa (p. ej. DialogRedeem) → <div> no interactivo (ni focusable ni accionable).
  if (onClick) {
    return (
      <button className={className} onClick={handleActionClick} type="button">
        {content}
      </button>
    );
  }

  return <div className={className}>{content}</div>;
};

BookCard.propTypes = {
  borderRadiusSize: PropTypes.oneOf(['default', 'big']),
  // Lo resuelve la vista (useCanShowAiProgramTag): sin él, como en los selectores y el canje, no hay etiqueta.
  canShowAiTag: PropTypes.bool,
  configProgramPicture: PropTypes.bool,
  customRight: PropTypes.node,
  has_tangerine_ai: PropTypes.number,
  image: PropTypes.string,
  isAccessible: PropTypes.bool,
  isEdited: PropTypes.bool,
  language: PropTypes.string,
  onChangeSelected: PropTypes.func,
  onClick: PropTypes.func,
  program: PropTypes.shape({ has_tangerine_ai: PropTypes.number }),
  program_guid: PropTypes.string,
  responsive: PropTypes.bool,
  selected: PropTypes.bool,
  t: PropTypes.func,
  title: PropTypes.string,
  versionAuthorAvatar: PropTypes.string,
  versionName: PropTypes.bool,
  view: PropTypes.oneOf(['default', 'selectable', 'custom-right', 'selectable disabled']),
};

BookCard.defaultProps = {
  isEdited: false,
  responsive: true,
  title: '',
  view: 'default',
  selected: false,
  onChangeSelected: function () {},
  borderRadiusSize: 'default',
};

export default withTranslation(['course', 'types-resources', 'languages'])(BookCard);
